import { useState, useRef } from "react";
import { MapPin, ToggleLeft, ToggleRight, User, Phone } from "lucide-react";

/**
 * Send the whole mailing to one address instead of to the post offices.
 *
 * Address entry is manual. The Base44 version injected the Google Maps script
 * into the page with the Places key in the URL, which meant the key shipped to
 * every browser and could be lifted from the network tab. Address lookup now
 * belongs on the server, where the key stays put — see the post office lookup
 * work for the shared component that will replace this form's manual entry.
 */
export default function ShippingOverrideSection({ value, onChange }) {
  const [enabled, setEnabled] = useState(value?.enabled || false);
  const [form, setForm] = useState({
    customer_name: value?.customer_name || "",
    address_line1: value?.address_line1 || "",
    city: value?.city || "",
    state: value?.state || "",
    zip: value?.zip || "",
    phone: value?.phone || ""
  });
  const addressInputRef = useRef(null);

  const notifyChange = (isEnabled, formData) => {
    onChange({
      enabled: isEnabled,
      ...formData
    });
  };

  const handleToggle = () => {
    const newEnabled = !enabled;
    setEnabled(newEnabled);
    notifyChange(newEnabled, form);
  };

  const handleFormChange = (field, val) => {
    const newForm = { ...form, [field]: val };
    setForm(newForm);
    notifyChange(enabled, newForm);
  };

  return (
    <div className="form-section" style={{ marginBottom: "1.5rem" }}>
      {/* Header / Toggle */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
        <div>
          <div className="form-title" style={{ marginBottom: "0.25rem" }}>
            <MapPin size={18} style={{ display: "inline", marginRight: "0.5rem", color: enabled ? "#dc2626" : "#64748b" }} />
            Override Shipping Address
          </div>
          <p style={{ color: "#64748b", fontSize: "0.8rem" }}>
            {enabled
              ? "All boxes will ship to the customer address below instead of individual post offices."
              : "Off — boxes ship to their individual post offices."}
          </p>
        </div>
        <button
          type="button"
          onClick={handleToggle}
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: enabled ? "#dc2626" : "#94a3b8",
            transition: "color 0.2s",
            flexShrink: 0
          }}
          title={enabled ? "Disable override" : "Enable override"}
        >
          {enabled
            ? <ToggleRight size={44} />
            : <ToggleLeft size={44} />}
        </button>
      </div>

      {enabled && (
        <div style={{
          background: "#fef2f2",
          border: "2px solid #fca5a5",
          borderRadius: "10px",
          padding: "1.25rem",
          marginTop: "0.5rem"
        }}>
          <div style={{
            fontSize: "0.8rem",
            fontWeight: 600,
            color: "#dc2626",
            marginBottom: "1rem",
            display: "flex",
            alignItems: "center",
            gap: "0.375rem"
          }}>
            <MapPin size={14} />
            Customer Delivery Address — used on ALL shipping labels
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
            <div className="form-group">
              <label className="form-label" style={{ fontSize: "0.8rem" }}>
                <User size={12} style={{ display: "inline", marginRight: "0.25rem" }} />
                Customer Name *
              </label>
              <input
                type="text"
                className="form-input"
                value={form.customer_name}
                onChange={(e) => handleFormChange("customer_name", e.target.value)}
                placeholder="e.g., Acme Print Co."
                style={{ fontSize: "0.875rem" }}
              />
            </div>
            <div className="form-group">
              <label className="form-label" style={{ fontSize: "0.8rem" }}>
                <Phone size={12} style={{ display: "inline", marginRight: "0.25rem" }} />
                Phone (optional)
              </label>
              <input
                type="text"
                className="form-input"
                value={form.phone}
                onChange={(e) => handleFormChange("phone", e.target.value)}
                placeholder="e.g., 555-123-4567"
                style={{ fontSize: "0.875rem" }}
              />
            </div>
          </div>

          <div className="form-group" style={{ marginTop: "0.75rem" }}>
            <label className="form-label" style={{ fontSize: "0.8rem" }}>Street Address *</label>
            <input
              ref={addressInputRef}
              type="text"
              className="form-input"
              value={form.address_line1}
              onChange={(e) => handleFormChange("address_line1", e.target.value)}
              placeholder="Start typing to search address..."
              style={{ fontSize: "0.875rem" }}
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 80px 100px", gap: "0.75rem", marginTop: "0.75rem" }}>
            <div className="form-group">
              <label className="form-label" style={{ fontSize: "0.8rem" }}>City *</label>
              <input
                type="text"
                className="form-input"
                value={form.city}
                onChange={(e) => handleFormChange("city", e.target.value)}
                placeholder="City"
                style={{ fontSize: "0.875rem" }}
              />
            </div>
            <div className="form-group">
              <label className="form-label" style={{ fontSize: "0.8rem" }}>State *</label>
              <input
                type="text"
                className="form-input"
                value={form.state}
                onChange={(e) => handleFormChange("state", e.target.value)}
                placeholder="IN"
                maxLength={2}
                style={{ fontSize: "0.875rem", textTransform: "uppercase" }}
              />
            </div>
            <div className="form-group">
              <label className="form-label" style={{ fontSize: "0.8rem" }}>ZIP *</label>
              <input
                type="text"
                className="form-input"
                value={form.zip}
                onChange={(e) => handleFormChange("zip", e.target.value)}
                placeholder="46528"
                style={{ fontSize: "0.875rem" }}
              />
            </div>
          </div>

          {form.address_line1 && form.city && form.state && form.zip && (
            <div style={{
              marginTop: "0.75rem",
              padding: "0.6rem 0.75rem",
              background: "#fee2e2",
              borderRadius: "6px",
              fontSize: "0.8rem",
              color: "#991b1b"
            }}>
              <strong>Labels will ship to:</strong> {form.customer_name || "Customer"}, {form.address_line1}, {form.city}, {form.state} {form.zip}
            </div>
          )}
        </div>
      )}
    </div>
  );
}